import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { Field, Select, Spinner } from '@aeris/ui'

import { fetchCashTerminals } from '../lib/api.ts'

/**
 * Choosing the till.
 *
 * This was a text input until F025, and that was the defect the whole feature
 * turned on: `terminal_id` is a plain string on the session, so a typo did not
 * raise an error — it opened a SECOND session on what everybody believed was
 * one drawer, and the day's cash ended up split across two shifts that
 * reconciled to nothing.
 *
 * A finite set the person must land on exactly, therefore a `Select`. Not a
 * `Combobox`: a shop has two or five tills, not eight thousand parties, and a
 * search box over five options is a search box that gets typed into.
 *
 * The remembered value (see `lib/remembered-terminal.ts`) is a memory of the
 * last choice and not the source of the value: if the till stored on this
 * machine is no longer in the register — archived, or a machine moved between
 * shops — the select opens empty rather than carrying a ghost into the
 * abertura.
 */

export type TerminalSelectProps = {
  value: string
  onValueChange: (code: string) => void
  label?: string
  className?: string
  autoFocus?: boolean
}

export const TerminalSelect = ({
  value,
  onValueChange,
  label = 'Caixa',
  className = 'w-56',
  autoFocus = false,
}: TerminalSelectProps): ReactNode => {
  const query = useQuery({
    queryKey: ['cash', 'terminals', 'active'],
    queryFn: ({ signal }) =>
      fetchCashTerminals({ status: 'active', sort: 'code' }, signal),
  })

  const terminals = query.data?.items ?? []
  const known = terminals.some((terminal) => terminal.code === value)

  if (query.isPending) {
    return (
      <Field label={label} className={className}>
        {() => (
          <div className="flex h-(--control-height-md) items-center">
            <Spinner />
          </div>
        )}
      </Field>
    )
  }

  // An empty register is not an error and not an empty dropdown either: it is
  // a shop that has not said which drawers it has, and the way out is a link
  // rather than a message that leaves somebody stuck.
  if (!query.isError && terminals.length === 0) {
    return (
      <Field label={label} hint="Nenhum caixa cadastrado" className={className}>
        {() => (
          <p className="text-(length:--text-sm) text-(--text-secondary)">
            Cadastre os caixas da filial em{' '}
            <Link
              to="/terminais"
              className="text-(--brand-primary) underline underline-offset-2"
            >
              Terminais
            </Link>
            .
          </p>
        )}
      </Field>
    )
  }

  return (
    <Field
      label={label}
      hint={query.isError ? 'Não foi possível carregar os caixas' : undefined}
      className={className}
    >
      {(field) => (
        <Select
          {...field}
          autoFocus={autoFocus}
          invalid={query.isError}
          value={known ? value : ''}
          onChange={(event) => {
            onValueChange(event.target.value)
          }}
        >
          <option value="">Selecione…</option>
          {terminals.map((terminal) => (
            <option key={terminal.id} value={terminal.code}>
              {terminal.code === terminal.name
                ? terminal.code
                : `${terminal.code} — ${terminal.name}`}
            </option>
          ))}
        </Select>
      )}
    </Field>
  )
}
